import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import ComInput from "../../../Components/ComInput/ComInput";
import ComButton from "../../../Components/ComButton/ComButton";
import { putData } from "../../../api/api";
import { useStorage } from "../../../hooks/useLocalStorage";
import { useNotification } from "../../../Notification/Notification";

const passwordSchema = yup.object({
  oldPassword: yup.string().required("Old password cannot be empty"),
  newPassword: yup
    .string()
    .min(8, "New password must be at least 8 characters")
    .matches(/[A-Z]/, "Must contain at least 1 uppercase letter")
    .matches(/[a-z]/, "Must contain at least 1 lowercase letter")
    .matches(/[0-9]/, "Must contain at least 1 number")
    .notOneOf(
      [yup.ref("oldPassword")],
      "Cannot be the same as the old password"
    )
    .required("New password cannot be empty"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Passwords must match")
    .required("Confirm password cannot be empty"),
});

export default function ChangePassword({}) {
  const navigate = useNavigate();
  const [isOldPasswordIncorrect, setIsOldPasswordIncorrect] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [user] = useStorage("use", null);
  const { notificationApi } = useNotification();

  const methods = useForm({
    resolver: yupResolver(passwordSchema),
    defaultValues: { oldPassword: "", newPassword: "", confirmPassword: "" },
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    setError,
    clearErrors,
  } = methods;

  const newPassword = watch("newPassword");
  const confirmPassword = watch("confirmPassword");
  const passwordStrength = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    lowercase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
  };

  useEffect(() => {
    if (newPassword !== confirmPassword) {
      setError("confirmPassword", {
        type: "manual",
        message: "Passwords must match",
      });
    } else {
      clearErrors("confirmPassword");
    }
  }, [newPassword, confirmPassword, setError, clearErrors]);

  const onSubmit = (data) => {
    setDisabled(true);
    setIsOldPasswordIncorrect(false);

    putData(`/accounts`, user.id, data)
      .then((response) => {
        console.log("Password changed successfully:", response);
        notificationApi("success", "Password changed successfully", "");
      })
      .catch((error) => {
        console.error("Error changing password:", error);
        if (error.response && error.response.status === 400) {
          setIsOldPasswordIncorrect(true);
        } else {
          notificationApi("error", "Failed to change password", "");
        }
      })
      .finally(() => {
        setDisabled(false);
      });
  };

  const handleCancelClick = () => {
    // Implement your cancel logic here, e.g., navigate back
  };

  return (
    <div className="pb-4 mb-4 mt-2 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="flex flex-col md:flex-row gap-10">
        <div className="p-10 sm:flex w-full rounded-md  ">
          <div className="flex flex-1 flex-col w-full gap-2    ">
            <div className="text-5xl font-semibold font-montserrat text-gray-500 mb-4">
              Change Password
            </div>
            <div className="text-xl font-montserrat text-gray-400">
              Password must contain:
            </div>
            <div
              className={`text-lg ${
                passwordStrength.length
                  ? "line-through text-gray-400"
                  : "text-red-400"
              }`}
            >
              {!passwordStrength.length ? (
                <CloseOutlined style={{ marginRight: "5px" }} />
              ) : (
                <CheckOutlined style={{ color: "blue", marginRight: "5px" }} />
              )}
              At least 8 characters
            </div>
            <div
              className={`text-lg ${
                passwordStrength.uppercase
                  ? "line-through text-gray-400"
                  : "text-red-400"
              }`}
            >
              {!passwordStrength.uppercase ? (
                <CloseOutlined style={{ marginRight: "5px" }} />
              ) : (
                <CheckOutlined style={{ color: "blue", marginRight: "5px" }} />
              )}
              At least 1 uppercase letter (A-Z)
            </div>
            <div
              className={`text-lg ${
                passwordStrength.lowercase
                  ? "line-through text-gray-400"
                  : "text-red-400"
              }`}
            >
              {!passwordStrength.lowercase ? (
                <CloseOutlined style={{ marginRight: "5px" }} />
              ) : (
                <CheckOutlined style={{ color: "blue", marginRight: "5px" }} />
              )}
              At least 1 lowercase letter (a-z)
            </div>
            <div
              className={`text-lg ${
                passwordStrength.number
                  ? "line-through text-gray-400"
                  : "text-red-400"
              }`}
            >
              {!passwordStrength.number ? (
                <CloseOutlined style={{ marginRight: "5px" }} />
              ) : (
                <CheckOutlined style={{ color: "blue", marginRight: "5px" }} />
              )}
              At least 1 number (0-9)
            </div>
          </div>
          <div className="flex-1 w-full md:w-1/2">
            <FormProvider {...methods}>
              <form
                className="flex flex-col gap-4"
                onSubmit={handleSubmit(onSubmit)}
              >
                <ComInput
                  placeholder="Enter your current password"
                  label="Current Password"
                  type="password"
                  maxLength={16}
                  {...register("oldPassword")}
                  required
                />
                {isOldPasswordIncorrect && (
                  <span className="text-red-500">
                    Old password is incorrect.
                  </span>
                )}
                <ComInput
                  placeholder="Enter your new password"
                  label="New Password"
                  type="password"
                  maxLength={16}
                  {...register("newPassword")}
                  error={errors.newPassword?.message}
                  required
                />

                <ComInput
                  placeholder="Re-enter your new password"
                  label="Confirm Password"
                  type="password"
                  maxLength={16}
                  {...register("confirmPassword")}
                  error={errors.confirmPassword?.message}
                  required
                />

                <ComButton
                  className="w-full"
                  htmlType="submit"
                  disabled={disabled}
                  type="primary"
                >
                  Change Password
                </ComButton>
                {/* <ComButton
                  className="w-full bg-transparent border-none shadow-none bg-white"
                  textColor={"text-black"}
                  onClick={handleCancelClick}
                >
                  Cancel
                </ComButton> */}
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
    </div>
  );
}
